import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { JsonApiModelInterface } from 'pf-frontend-common/src/module/json-api/model.interface';

import { BackendConfigCountryInterface } from 'common/data/backend/config/country.interface';
import { CountryModel } from 'common/data/country/model';
import { CountrySortEnum } from 'common/data/country/sort.enum';
import { LeadModelInterface } from 'common/data/lead/model.interface';
import { LeadStoreCreateResponseInterface } from 'common/data/lead/store/create/response.interface';
import { UserAutoRegisterModel } from 'common/data/user/auto-register/model';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { phoneWithoutIdd } from 'common/helper/phone/without-idd';
import { DataStore } from 'common/module/data/store';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputStateInterface } from 'common/module/input/state.interface';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { LeadEmailFormContactInputFieldNameEnum } from 'common/module/lead/email/form/contact/input-field-name.enum';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormInputAdapter } from 'common/module/lead/email/form/input-adapter';
import { LeadEmailFormStatechartEvent } from 'common/module/lead/email/form/statechart.event';
import { LeadEmailFormStatechartActionEnum } from 'common/module/lead/email/form/statechart/action.enum';
import { LeadEmailFormStrategyInterface } from 'common/module/lead/email/form/strategy.interface';
import { LeadEmailFormStrategyAdapterInterface } from 'common/module/lead/email/form/strategy/adapter.interface';
import { LeadEmailFormStrategyStoreInterface } from 'common/module/lead/email/form/strategy/store.interface';
import { LeadEmailFormViewStoreEvent } from 'common/module/lead/email/form/view-store.event';
import { LeadEmailFormViewStoreOptionsInterface } from 'common/module/lead/email/form/view-store-options.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { TelephoneInputCountryInterface } from 'common/module/telephone-input/country.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { CountryStoreServiceInterface } from 'common/service/country-store/service.interface';
import { LeadEmailFormContactServiceInterface } from 'common/service/lead-email-form-contact/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { LeadEmailFormTemplatePropsInterface } from './template-props.interface';

export class LeadEmailFormViewStore extends DataStore {
  /**
   * Form view-store state
   */
  protected state: LeadEmailFormTemplatePropsInterface;

  /**
   * Current strategy (set on runtime)
   */
  protected strategy: LeadEmailFormStrategyInterface;

  /**
   * Model related to the lead form
   */
  protected model: JsonApiModelInterface;

  /**
   * Lead model
   */
  protected leadModel: LeadModelInterface;

  /**
   * Is countries list loaded?
   */
  protected isCountriesListLoaded: boolean = false;

  /**
   * Countries list
   */
  protected countriesList: TelephoneInputCountryInterface[] = [];

  /**
   * Constructor
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected captchaViewStore: GoogleRecaptchaViewStore,
    protected countryConfig: BackendConfigCountryInterface,
    private leadEmailViewStoreStatechart: StatechartStoreInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private statsService: StatsServiceInterface,
    private leadEmailFormContactService: LeadEmailFormContactServiceInterface,
    private countryStoreService: CountryStoreServiceInterface
  ) {
    super(eventEmitter);
  }

  /**
   * Sets the strategy
   */
  public setStrategy(strategy: LeadEmailFormStrategyInterface): void {
    this.strategy = strategy;
  }

  /**
   * Returns the strategy
   */
  public getStrategy(): LeadEmailFormStrategyInterface {
    if (!this.strategy) {
      throw new Error('LeadEmailFormViewStore.strategy has not been set before calling getStrategy()');
    }

    return this.strategy;
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    const strategyInitialState = this.getStrategy().getInitialState();

    const state: LeadEmailFormTemplatePropsInterface = {
      isSignInComponentVisible: false,
      input: {
        name: { ...strategyInitialState.input.name },
        email: { ...strategyInitialState.input.email },
        phone: { ...strategyInitialState.input.phone },
        message: { ...strategyInitialState.input.message },
        captcha: { ...strategyInitialState.input.captcha },
      },
      status: { ...strategyInitialState.status },
      formTitle: strategyInitialState.formTitle,
      error: strategyInitialState.error,
      model: this.model,
      captcha: this.captchaViewStore.getState(),
      onComponentDidMount: this.onComponentDidMount,
      onChange: this.onChange,
      onClickChangeCountryCode: this.onClickChangeCountryCode,
      onCheck: this.onCheck,
      onBlur: this.onBlur,
      onSubmit: this.onSubmit,
      onClose: this.onClose,
      countriesList: this.countriesList,
      isCountriesListLoaded: this.isCountriesListLoaded,
    };

    // Optional input: create alert
    if (strategyInitialState.input.createAlert) {
      state.input.createAlert = { ...strategyInitialState.input.createAlert };
    }

    if (strategyInitialState.input.acceptTerms) {
      state.input.acceptTerms = { ...strategyInitialState.input.acceptTerms };
    }

    if (strategyInitialState.input.receiveAdvertising) {
      state.input.receiveAdvertising = { ...strategyInitialState.input.receiveAdvertising };
    }

    // Optional input: enquiry type
    if (strategyInitialState.input.enquiryType) {
      state.input.enquiryType = { ...strategyInitialState.input.enquiryType };
    }

    // Optionally pass in the mortgage campaign id
    if (this.countryConfig.mortgage && this.countryConfig.mortgage.campaign.property_lead_confirm) {
      state.mortgageCampaignId = this.countryConfig.mortgage.campaign.property_lead_confirm;
    }

    // Set state
    this.state = state;
  }

  /**
   * @inheritDoc
   */
  public initialize(options?: LeadEmailFormViewStoreOptionsInterface): void {
    if (options && options.model) {
      this.model = options.model;
    }

    // Init strategy
    this.getStrategy().initialize({
      model: this.model,
    });

    this.initState();

    // Attach listeners
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public getState(): LeadEmailFormTemplatePropsInterface {
    return this.state;
  }

  /**
   * @inheritDoc
   */
  public updateModel(model: JsonApiModelInterface): void {
    const state = this.getState();

    this.model = model;
    state.model = model;

    // Update state
    this.setState(state, this.updateModel);
  }

  /**
   * Returns the model
   */
  public getModel(): JsonApiModelInterface {
    return this.model;
  }

  /**
   * Attach listeners
   */
  private observers(): void {
    // Captcha
    this.captchaViewStore
      .getEventEmitter()
      .addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptchaViewStore);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.alreadyRegistered, this.onAlreadyRegisteredUserAutentication);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.autoRegistrationFailed, this.onAutoRegistrationFailedUserAutentication);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationSucceeded, this.onRegistrationSucceededUserAuthentication);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInSucceeded, this.onSignInSucceededUserAuthentication);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.showSignInMessage, this.onShowSignInMessageStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.showSuccessMessage, this.onShowSuccessMessageStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.sendAutoRegisterRequest, this.onSendAutoRegisterRequestStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.showErrors, this.onShowErrorsStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.sendRequestToCreateLead, this.onSendRequestToCreateLeadStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.leadEmailViewStoreStatechart
      .getEventEmitter()
      .addListener(LeadEmailFormStatechartActionEnum.resetCaptcha, this.onResetCaptchaActionStatechart);
  }

  /**
   * @inheritdoc
   */
  private onComponentDidMount = () => {
    // Load countries async
    this.loadCountries();
  };

  /**
   * Load countries list into state
   */
  private loadCountries(): Promise<void> {
    return this.countryStoreService
      .getAll(CountrySortEnum.priority)
      .then((countriesList) => {
        // Get only countries with phone codes
        this.state.countriesList = this.countriesList = this.getCountries(countriesList);
        this.setState(this.state);
      })
      .catch(() => null)
      .then(() => {
        this.state.isCountriesListLoaded = this.isCountriesListLoaded = true;
        this.setState(this.state);
      });
  }

  /**
   * Get list of countries that have phone codes
   */
  private getCountries(countriesList: CountryModel[]): TelephoneInputCountryInterface[] {
    const countryList: TelephoneInputCountryInterface[] = [];

    countriesList.map((country) => {
      if (country.phoneCode) {
        countryList.push({
          name: country.name,
          phoneCode: country.phoneCode,
          countryCode: country.code,
        });
      }
    });
    return countryList;
  }

  /**
   * Validate the input and returns error message if any
   */
  private validateInput(input: InputStateInterface): string {
    // Input has validators
    if (input.validators) {
      if (typeof input.value === 'string') {
        input.value = input.value.trim();
      }

      const errors: string[] = input.validators
        .map((validator: InputValidatorInterface) => validator.getError(input.value, this.state.input))
        .filter((message: string) => !!message);

      // Only keep first error if any
      return errors[0] || '';
    }

    return '';
  }

  /**
   * Submit the form to the API
   */
  private submit(): void {
    const state = {
      ...this.state,
    };

    state.status.submitting = true;
    state.error = '';

    this.setState(state);

    this.leadModel = this.getAdapter().createModelFromLeadEmailForm(this.state.input);

    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.wantToSendLead,
      payload: this.leadModel,
    });
  }

  /**
   * Get adapter
   */
  private getAdapter(): LeadEmailFormStrategyAdapterInterface {
    if (!this.getStrategy().adapter) {
      throw new Error("Can't get LeadEmailFormViewStore.strategy adapter");
    }
    return this.getStrategy().adapter;
  }

  /**
   * Get store
   */
  private getStore(): LeadEmailFormStrategyStoreInterface {
    if (!this.getStrategy().store) {
      throw new Error("Can't get LeadEmailFormViewStore.strategy store");
    }
    return this.getStrategy().store;
  }

  /**
   * Get phone code from countries list
   */
  private getPhoneCodeFromCountriesList(code: string): string {
    const phoneCode = this.state.countriesList.find((country) => country.countryCode === code)?.phoneCode;
    return phoneCode ? `+${phoneCode}` : '';
  }

  /**
   * Change input value
   */
  private onChange = (e: Event | string, fieldName: keyof LeadEmailFormInputInterface) => {
    const state: LeadEmailFormTemplatePropsInterface = {
      ...this.state,
    };
    const input = state.input[fieldName];

    if (!input) {
      throw new Error(`Input ${fieldName} not found`);
    }

    let value = typeof e === 'string' ? e : (<HTMLInputElement>e.target).value;
    input.value = value;
    this.leadEmailFormContactService.setData(fieldName, input.value);

    if (fieldName === LeadEmailFormContactInputFieldNameEnum.phone) {
      value = phoneWithoutIdd(value);

      const findCountryFromValue = this.state.countriesList.find(
        (country) => country.phoneCode && value.startsWith(country.phoneCode)
      );

      if (findCountryFromValue) {
        state.input.phone.countryCode = findCountryFromValue.countryCode;
        this.leadEmailFormContactService.setData(
          LeadEmailFormContactInputFieldNameEnum.countryCode,
          findCountryFromValue.countryCode
        );
      }
    }

    this.setState(state, this.onChange);
  };

  /**
   * On click change country code
   */
  private onClickChangeCountryCode = (countryCode: string) => {
    const state: LeadEmailFormTemplatePropsInterface = {
      ...this.state,
    };
    const phoneNumber = state.input.phone.value.replace('00', '').replace('+', '');
    const previouslySelectedCountry = state.countriesList.find(
      (country) => country.phoneCode && phoneNumber.startsWith(country.phoneCode)
    );

    if (previouslySelectedCountry) {
      state.input.phone.value = phoneNumber.replace(
        previouslySelectedCountry.phoneCode,
        this.getPhoneCodeFromCountriesList(countryCode)
      );
    }

    state.input.phone.countryCode = countryCode;
    this.leadEmailFormContactService.setData(LeadEmailFormContactInputFieldNameEnum.countryCode, countryCode);
    this.setState(state, this.onClickChangeCountryCode);
  };

  /**
   * Check checkbox
   */
  private onCheck = (e: Event, fieldName: keyof LeadEmailFormInputInterface) => {
    const state: LeadEmailFormTemplatePropsInterface = {
      ...this.state,
    };
    const input = state.input[fieldName];

    if (!input) {
      throw new Error(`Input ${fieldName} not found`);
    }

    input.value = (<HTMLInputElement>e.target).checked;
    this.setState(state, this.onCheck);
  };

  /**
   * Blur form element
   */
  private onBlur = (e: Event, fieldName: keyof LeadEmailFormInputInterface) => {
    if (this.state.status.submitted) {
      return;
    }

    const state: LeadEmailFormTemplatePropsInterface = {
      ...this.state,
    };
    const input = state.input[fieldName];

    if (!input) {
      throw new Error(`Input ${fieldName} not found`);
    }

    input.error = this.validateInput(input);
    this.setState(state, this.onBlur);
  };

  /**
   * Submit form
   */
  private onSubmit = (e: Event) => {
    e.preventDefault();

    const state: LeadEmailFormTemplatePropsInterface = {
      ...this.state,
    };

    // Validate all inputs
    const errors = Object.keys(this.state.input)
      .map((name: keyof LeadEmailFormInputInterface) => {
        const input = this.state.input[name];
        input.error = this.validateInput(input);

        return input;
      })
      .filter((input: InputStateInterface) => input.error);

    this.setState(state, this.onSubmit);

    // Form is valid
    if (errors.length === 0) {
      this.submit();
    }
  };

  /**
   * Show sign in message and button
   */
  private onShowSignInMessageStatechart = (): void => {
    this.state.isSignInComponentVisible = true;

    this.setState(this.state);
  };

  /**
   * Show success message
   */
  private onShowSuccessMessageStatechart = (): void => {
    // Change current status
    this.state.status.submitting = false;
    this.state.status.submitted = true;

    // Update state
    this.setState(this.state);

    // Emit event: submit
    this.eventEmitter.emit(LeadEmailFormViewStoreEvent.submit, this.state.input);

    // Update inputs using strategy
    this.getStrategy().updateInput(this.state.input);
  };

  /**
   * Show errors
   */
  private onShowErrorsStatechart = (response: Error | LeadStoreCreateResponseInterface) => {
    this.state.status.submitting = false;

    if (response instanceof Error) {
      this.setState(this.state);
      throw new Error(response.message);
    }

    if (this.state.input.captcha.validators.length) {
      // Captcha tokens can only be used once, so reset it
      this.captchaViewStore.reset();
    }

    // Has errors
    if (response.data.errors && response.data.errors.length) {
      // Convert response & input state into new input state
      this.state.input = new LeadEmailFormInputAdapter(response, this.state.input).getData();
    } else {
      this.state.error = i18nTranslate(
        'Sorry, an error occurred while processing your request, please try again in few minutes.'
      );
    }

    this.setState(this.state);
  };

  /**
   * Send request to create lead
   */
  private onSendRequestToCreateLeadStatechart = (leadModel: LeadModelInterface) => {
    this.getStore()
      .create(this.getModel().id, leadModel)
      .then(() => {
        this.leadEmailViewStoreStatechart.transit({
          event: LeadEmailFormStatechartEvent.leadCreateSucceeded,
          payload: leadModel,
        });
      })
      .catch((response) => {
        if (!leadModel.captcha_token && response.status === 449) {
          this.leadEmailViewStoreStatechart.transit({
            event: LeadEmailFormStatechartEvent.leadRetryWithCaptcha,
            payload: null,
          });
        } else {
          this.leadEmailViewStoreStatechart.transit({
            event: LeadEmailFormStatechartEvent.leadCreateFailed,
            payload: response,
          });
        }
      });
  };

  /**
   * Send auto register request to server
   */
  private onSendAutoRegisterRequestStatechart = (model: LeadModelInterface): void => {
    if (this.userAuthenticationService.getToken()) {
      this.leadEmailViewStoreStatechart.transit({
        event: LeadEmailFormStatechartEvent.userIsLoggedIn,
        payload: model,
      });

      return;
    }

    const autoregisterModel = new UserAutoRegisterModel();

    const nameArray = model.name.trim().split(' ');

    autoregisterModel.email = model.email;
    autoregisterModel.first_name = nameArray.shift();
    autoregisterModel.last_name = nameArray.join(' ');
    autoregisterModel.phone = model.phone;

    this.userAuthenticationService.autoRegister(autoregisterModel);
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (!this.state.input.captcha.value) {
      this.captchaViewStore.execute();

      return;
    }

    this.leadModel = this.getAdapter().createModelFromLeadEmailForm(this.state.input);

    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.captchaValueChanged,
      payload: this.leadModel,
    });
  };

  /**
   * Reset google captcha
   */
  private onResetCaptchaActionStatechart = () => {
    this.captchaViewStore.reset();
  };

  /**
   * User is already registered
   */
  private onAlreadyRegisteredUserAutentication = (): void => {
    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.userWasAlreadyRegistered,
      payload: this.leadModel,
    });
  };

  /**
   * User registration succeeded
   */
  private onRegistrationSucceededUserAuthentication = (): void => {
    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.userSuccessfulyRegistered,
      payload: this.leadModel,
    });

    this.getStrategy().setUserData();
  };

  /**
   * User signin succeeded
   */
  private onSignInSucceededUserAuthentication = (): void => {
    this.getStrategy().setUserData();
  };

  /**
   * User auto registration failed
   */
  private onAutoRegistrationFailedUserAutentication = (): void => {
    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.autoRegistrationFailed,
      payload: this.leadModel,
    });
  };

  /**
   * @inheritDoc
   */
  private onClose = () => {
    this.eventEmitter.emit(LeadEmailFormViewStoreEvent.close);

    if (this.state.isSignInComponentVisible && this.state.status.submitted) {
      this.statsService.ctaClick({
        cta: {
          authentication: 'login-popup-close-button',
        },
      });
    }
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptchaViewStore = () => {
    const state = this.getState();

    state.input.captcha.value = this.captchaViewStore.getState().value;

    this.setState(state);

    this.leadModel = this.getAdapter().createModelFromLeadEmailForm(this.state.input);

    this.leadEmailViewStoreStatechart.transit({
      event: LeadEmailFormStatechartEvent.captchaValueChanged,
      payload: this.leadModel,
    });
  };
}
